import {tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@helios/shared/components/button'
import Image from '@helios/shared/components/image'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Layout, Section, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type ImageSource from '@helios/shared/types/image'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

interface Props {
    headline: string
    items: Item[]
}

interface Item {
    title?: string
    description?: string
    image?: ImageSource
    ctaText?: string
    ctaLink?: PrismicLinkProps
}

export const ContactHero = ({headline, items}: Props) => (
    <BackgroundWrapper color="muted">
        <StyledSection>
            <Text as="h1" variant="heading-large" center>
                {headline}
            </Text>
            <StyledLayout as={Layout} variant="two-equal">
                {items.map((item, index) => (
                    <ContentBox
                        key={`content-box-${index}`}
                        trackingId={`Contact Hero CTA ${index + 1}`}
                        {...item}
                    />
                ))}
            </StyledLayout>
        </StyledSection>
    </BackgroundWrapper>
)

const ContentBox = ({
    trackingId,
    title,
    description,
    image,
    ctaLink,
    ctaText,
}: Item & {trackingId: string}) => (
    <ColumnWrapper>
        {image && <Image image={image} />}

        {(title || description) && (
            <CopyWrapper>
                {title && (
                    <Text as="h2" variant="heading-base">
                        {title}
                    </Text>
                )}

                {description && (
                    <Text variant="paragraph-base" color="muted">
                        {description}
                    </Text>
                )}
            </CopyWrapper>
        )}

        {ctaLink && ctaText && (
            <Button
                as={PrismicLink}
                variant="outline"
                {...ctaLink}
                onClick={() =>
                    track(TrackingEvent.ContactUsPageHeroLinkActioned, {
                        action: 'completed',
                        cta: trackingId,
                    })
                }
                data-testid="contact-hero-cta-link"
            >
                {ctaText}
            </Button>
        )}
    </ColumnWrapper>
)

const StyledSection = styled(Section)`
    display: grid;
    gap: ${tokens.spacing60};
`

const ColumnWrapper = styled.div`
    display: grid;
    justify-items: center;
    gap: ${tokens.spacing20};
`

const CopyWrapper = styled.div`
    display: grid;
    justify-items: center;
    gap: ${tokens.spacing4};
`

const StyledLayout = styled.div`
    grid-gap: 80px;

    ${mq.min('sm')} {
        grid-gap: ${tokens.spacing40};
    }

    ${mq.min('md')} {
        grid-gap: 80px;
    }
`

export const contactHeroSliceFragment = graphql`
    fragment ContactHeroSlice on PrismicSlicePageDataBodyContactHero {
        __typename
        primary {
            headline
        }
        items {
            title
            description
            image {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumns)
            }
            cta_text
            cta_link {
                ...PrismicLink
            }
        }
    }
`
